import { useCallback, useState } from "react";
import type { KeyboardEvent } from "react";

import { useTranslation } from "react-i18next";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, Header, Icon, Message, Modal } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import type { UrlOrganisation } from "./types";

const LabelledInput = styled(Input)`
  input {
    width: 0;
  }
`;

const OrganisationChooser = () => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const errorMessage = useAppSelector((state) => state.entry.error);
  const [searchCode, setSearchCode] = useState("");
  const [searching, setSearching] = useState(false);

  const onReceiveOrganisation = useCallback(
    (org: UrlOrganisation) => {
      dispatch(actions.organisations.receiveUrlOrganisation(org));
    },
    [dispatch],
  );
  const onError = useCallback(
    (value: string) => {
      dispatch(actions.entry.updateError(value));
    },
    [dispatch],
  );

  const openChatwoot = useCallback(() => {
    if (window.$chatwoot) {
      window.$chatwoot.toggle();
    }
  }, []);

  const searchByCode = () => {
    setSearching(true);
    onError("");
    api.organisations.getByCode(
      searchCode,
      (organisation) => {
        onReceiveOrganisation(organisation);
        setSearching(false);
      },
      () => {
        onError(t("entry.register.org.error"));
        setSearching(false);
      },
    );
  };

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      >
        <Modal.Header>What is my organisational code?</Modal.Header>
        <Modal.Content>
          <p>
            In order to sign up to {util.appName()} you will need an
            organisational code. This is a unique code that identifies your
            organisation.
          </p>
          <p>
            If you don't know this code then{" "}
            <span
              style={{ textDecorationLine: "underline", cursor: "pointer" }}
              onClick={() => openChatwoot()}
            >
              click here
            </span>{" "}
            or press the button below to start a chat with us. Tell us which
            organisation you are looking to join and we can support you to get
            started.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setHelpModalOpen(false)}>Close</Button>
          <Button onClick={() => openChatwoot()} primary>
            <Icon name="chat" />
            Speak to us
          </Button>
        </Modal.Actions>
      </Modal>

      <Header as="h2">
        Welcome to {util.appName()}
        <Header.Subheader>{t("entry.register.org.title")}</Header.Subheader>
      </Header>

      {errorMessage && <Message warning>{errorMessage}</Message>}

      <div
        style={{
          display: "flex",
          gap: 10,
          flexDirection: theme.sizes.isMobile ? "column" : "row",
        }}
      >
        <LabelledInput
          style={{ flex: 1 }}
          label=".simplydo.co.uk"
          labelPosition="right"
          name="orgCode"
          placeholder={t("entry.register.org.placeholder")}
          defaultValue={searchCode}
          onKeyDown={(e: KeyboardEvent) => e.keyCode === 13 && searchByCode()}
          onChange={(e) => setSearchCode(e.target.value)}
        />

        <Button
          fluid={theme.sizes.isMobile}
          loading={searching}
          disabled={!searchCode}
          primary
          onClick={searchByCode}
        >
          {t("generic.continue")}
          <Icon name="arrow right" />
        </Button>
      </div>
      <div style={{ marginTop: 15, marginBottom: 10, textAlign: "right" }}>
        <a
          href="#"
          role="button"
          tabIndex={0}
          onClick={() => setHelpModalOpen(true)}
          onKeyDown={handleClickForKeyboard(() => setHelpModalOpen(true))}
        >
          {t("entry.register.unknownCode")}
        </a>
      </div>
    </>
  );
};

export default OrganisationChooser;
